// Iter84 — Logistics Request Management (Phase 1A → iter85b Phase 1B-α polish).
//
// Single-page hub with three tabs:
//   1. Dashboard — Pricer + Request Handler counts + due-today/this-week.
//      KPI cards + by-type chips + by-stage rows are all clickable filters.
//   2. List — filterable + sortable + XLSX export + click-row → detail drawer.
//   3. Calendar — horizontally scrollable months of leadási határidő events.
//
// Phase 1B-α: theme-aware colors (CSS vars so light mode renders with full
// contrast instead of washed-out dark hex), clickable KPI tiles, clickable
// by-type chips, and a horizontal calendar that scrolls sideways through
// months instead of stacking vertically.
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Plus, FileText, Calendar as CalendarIcon, LayoutDashboard, List as ListIcon, X, ChevronLeft, ChevronRight, BarChart3, Crown, Sparkles } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  LR_STAGES, LR_TYPES, STAGE_LABELS, STAGE_COLORS,
  useLogisticsRequests, useLogisticsDashboard, useLogisticsCalendar,
  useAwaitingExecuted,
  useCreateLogistics, useTransitionLogistics, usePatchLogistics,
  useLogisticsCategories, useCreateLogisticsCategory,
  exportLogisticsXlsx, exportLogisticsPdf,
  useLogisticsStats, useTenderSchedule,
} from "../api/logistics";
import { useAdminUsers } from "../api/sitroom";
import { useAuth } from "../context/AuthContext";

const fmtMoney = (v, ccy) => v == null ? "—" : `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${ccy || "EUR"}`;
const fmtDate = (s) => s ? String(s).slice(0, 10) : "—";

export default function LogisticsRequests() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [filters, setFilters] = useState({ stage: "", request_type: "", customer: "", mine: false, assignee_role: "", due: "" });

  // Reset filters except the click-source; used when the user clicks a KPI
  // tile or by-type chip so previous filters don't muddy the navigation.
  const jumpToList = (next) => {
    setFilters({ stage: "", request_type: "", customer: "", mine: false, assignee_role: "", due: "", ...next });
    setTab("list");
  };

  return (
    <div className="px-6 py-6" data-testid="logistics-requests-page" style={{ background: "var(--bg-base)", color: "var(--text-primary)", minHeight: "100vh" }}>
      <Header tab={tab} setTab={setTab} onCreate={() => navigate("/logistics-requests/new")} onChatCreate={() => navigate("/logistics-requests/intake-chat")} onExport={() => exportLogisticsXlsx(filters)} />

      {tab === "dashboard" && <DashboardTab onJump={jumpToList} />}
      {tab === "list" && (
        <ListTab
          filters={filters} setFilters={setFilters}
          onSelect={(r) => navigate(`/logistics-requests/${r.id}`)}
        />
      )}
      {tab === "calendar" && <CalendarTab onSelect={(rid) => navigate(`/logistics-requests/${rid}`)} />}
      {tab === "stats" && <StatsTab />}
      {tab === "key_account" && <KeyAccountTab onJump={jumpToList} />}
      {tab === "tenders" && <TenderScheduleTab onSelect={(rid) => navigate(`/logistics-requests/${rid}`)} />}
    </div>
  );
}

// ───────────────────────────── header ─────────────────────────────

const Header = ({ tab, setTab, onCreate, onChatCreate, onExport }) => (
  <div className="mb-5">
    <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
      <div>
        <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 22, color: "var(--text-primary)", letterSpacing: "-0.4px" }}>
          Logistics Requests
        </h1>
        <p style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)", letterSpacing: "0.5px", textTransform: "uppercase", marginTop: 2 }}>
          Tender · Spot · Sonderfahrt · Phase 1A
        </p>
      </div>
      <div className="flex gap-2 flex-wrap">
        <button type="button" onClick={onExport} className="inline-flex items-center gap-2 px-3 py-2 rounded" style={btn("blue")} data-testid="logistics-export-xlsx">
          <Download size={13} /> EXPORT XLSX
        </button>
        {/* γ-3-I — SITREP AI intake chat. Sibling to the standard form path so
            users see both options at once: conversational vs structured. */}
        <button type="button" onClick={onChatCreate} className="inline-flex items-center gap-2 px-3 py-2 rounded" style={btn("purple")} data-testid="logistics-chat-create-btn">
          <Sparkles size={13} /> CHAT TO CREATE
        </button>
        <button type="button" onClick={onCreate} className="inline-flex items-center gap-2 px-3 py-2 rounded" style={btn("green")} data-testid="logistics-new-btn">
          <Plus size={13} /> NEW REQUEST
        </button>
      </div>
    </div>
    <div className="flex gap-1" style={{ borderBottom: "1px solid var(--border-strong)" }}>
      <Tab label="Dashboard" icon={LayoutDashboard} active={tab === "dashboard"} onClick={() => setTab("dashboard")} testid="logistics-tab-dashboard" />
      <Tab label="List" icon={ListIcon} active={tab === "list"} onClick={() => setTab("list")} testid="logistics-tab-list" />
      <Tab label="Calendar" icon={CalendarIcon} active={tab === "calendar"} onClick={() => setTab("calendar")} testid="logistics-tab-calendar" />
      <Tab label="Statistics" icon={BarChart3} active={tab === "stats"} onClick={() => setTab("stats")} testid="logistics-tab-stats" />
      <Tab label="Key Account" icon={Crown} active={tab === "key_account"} onClick={() => setTab("key_account")} testid="logistics-tab-key-account" />
      <Tab label="Tenders" icon={CalendarIcon} active={tab === "tenders"} onClick={() => setTab("tenders")} testid="logistics-tab-tenders" />
    </div>
  </div>
);

const btn = (color) => ({
  background: color === "green" ? "#22a35a" : color === "purple" ? "#7e3aa8" : "#185FA5",
  border: `1px solid ${color === "green" ? "#0d6b39" : color === "purple" ? "#4f1e6a" : "#0d3f78"}`,
  color: "#ffffff",
  fontFamily: "Syne, sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.5px", cursor: "pointer",
  textShadow: "0 1px 0 rgba(0,0,0,0.18)",
});

const Tab = ({ label, icon: Icon, active, onClick, testid }) => (
  <button
    type="button" onClick={onClick}
    className="inline-flex items-center gap-2 px-3 py-2"
    style={{
      background: "transparent", border: "none",
      borderBottom: `2px solid ${active ? "#185FA5" : "transparent"}`,
      color: active ? "var(--text-primary)" : "var(--text-dimmer)",
      fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.5px", cursor: "pointer",
    }}
    data-testid={testid}
  >
    <Icon size={13} /> {label}
  </button>
);

// ───────────────────────────── dashboard ─────────────────────────────

const DashboardTab = ({ onJump }) => {
  const dash = useLogisticsDashboard();
  const d = dash.data || {};
  const awaiting = useAwaitingExecuted();
  const navigate = useNavigate();
  const awaitingItems = awaiting.data?.items || [];
  const awaitingCount = d.awaiting_executed ?? awaitingItems.length;
  return (
    <div className="space-y-4" data-testid="logistics-dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Assigned to me (Pricer)"
          value={d.assigned_to_me_as_pricer ?? 0}
          accent="#7e3aa8"
          onClick={() => onJump({ assignee_role: "pricer" })}
          testid="logistics-stat-pricer"
        />
        <StatCard
          label="Assigned to me (Handler)"
          value={d.assigned_to_me_as_handler ?? 0}
          accent="#185FA5"
          onClick={() => onJump({ assignee_role: "handler" })}
          testid="logistics-stat-handler"
        />
        <StatCard
          label="Due today"
          value={d.my_due_today ?? 0}
          accent="#b22a29"
          onClick={() => onJump({ due: "today", mine: true })}
          testid="logistics-stat-due-today"
        />
        <StatCard
          label="Due this week"
          value={d.my_due_this_week ?? 0}
          accent="#b7770d"
          onClick={() => onJump({ due: "week", mine: true })}
          testid="logistics-stat-due-week"
        />
      </div>
      {/* γ-3-G — Awaiting Executed nudge. Won deals whose last drop date has
          passed but the user has NOT marked them as Megvalósult yet. Manual
          step (no auto-advance — a won deal's drop date passing does NOT
          mean the freight actually moved) — but nothing should fall through
          the cracks either. */}
      {awaitingCount > 0 && (
        <div className="rounded-md p-3" style={{ background: "rgba(13,107,57,0.06)", border: "1px solid rgba(13,107,57,0.40)", borderLeft: "3px solid #0d6b39" }} data-testid="logistics-awaiting-executed-section">
          <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
            <div>
              <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13, color: "#0d6b39", letterSpacing: "0.4px" }}>
                Awaiting Executed status update
              </div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)", marginTop: 2 }}>
                {awaitingCount} won {awaitingCount === 1 ? "deal" : "deals"} past last dropoff — confirm by marking as <strong>Megvalósult</strong>.
              </div>
            </div>
            <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 24, color: "#0d6b39" }} data-testid="logistics-awaiting-executed-count">
              {awaitingCount}
            </span>
          </div>
          <div className="space-y-1" data-testid="logistics-awaiting-executed-list">
            {awaitingItems.slice(0, 5).map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={() => navigate(`/logistics-requests/${it.id}`)}
                className="w-full flex items-center justify-between px-2 py-1.5 rounded transition-colors"
                style={{ background: "var(--bg-surface)", border: "1px solid var(--border-strong)", cursor: "pointer", textAlign: "left" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(13,107,57,0.08)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "var(--bg-surface)")}
                data-testid={`logistics-awaiting-executed-row-${it.id}`}
              >
                <span className="flex-1 min-w-0">
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)" }}>{it.id}</span>
                  <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, color: "var(--text-primary)", marginLeft: 8 }}>
                    {it.deal_name || "—"}
                  </span>
                  {it.customer_name && (
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dim)", marginLeft: 8 }}>
                      · {it.customer_name}
                    </span>
                  )}
                </span>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#b22a29", letterSpacing: "0.4px" }}>
                  expected {String(it.expected_by || "").slice(0, 10)}
                </span>
              </button>
            ))}
            {awaitingItems.length > 5 && (
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "var(--text-dimmer)", textAlign: "center", paddingTop: 4 }}>
                + {awaitingItems.length - 5} more
              </div>
            )}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="By stage">
          <div className="space-y-1">
            {LR_STAGES.map((s) => {
              const count = (d.by_stage || {})[s] || 0;
              const isEmpty = count === 0;
              return (
                <button key={s} type="button" onClick={() => onJump({ stage: s })}
                  className="w-full flex items-center justify-between px-2 py-1.5 rounded transition-colors"
                  style={{ background: "var(--bg-elevated)", border: "1px solid var(--border-strong)", cursor: "pointer" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(24,95,165,0.10)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "var(--bg-elevated)")}
                  data-testid={`logistics-by-stage-${s}`}>
                  <span className="inline-flex items-center gap-2">
                    {/* iter87 γ-1 fix #4 — colored status dot + dark readable text;
                        text dims to var(--text-dimmer) when the stage is empty
                        so the eye is drawn to stages that have activity. */}
                    <span aria-hidden="true" data-testid={`logistics-by-stage-dot-${s}`}
                      style={{ display: "inline-block", width: 8, height: 8, borderRadius: 9999, background: STAGE_COLORS[s], opacity: isEmpty ? 0.45 : 1, flexShrink: 0 }} />
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: isEmpty ? "var(--text-dimmer)" : "var(--text-primary)", letterSpacing: "0.3px", fontWeight: 600 }}>
                      {STAGE_LABELS[s]}
                    </span>
                  </span>
                  <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: isEmpty ? "var(--text-dimmer)" : "var(--text-primary)" }}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </Card>
        <Card title="By type">
          <div className="space-y-1">
            {LR_TYPES.map((t) => (
              <button key={t} type="button" onClick={() => onJump({ request_type: t })}
                className="w-full flex items-center justify-between px-2 py-1.5 rounded transition-colors"
                style={{ background: "var(--bg-elevated)", border: "1px solid var(--border-strong)", cursor: "pointer" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(34,163,90,0.10)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "var(--bg-elevated)")}
                data-testid={`logistics-by-type-${t}`}>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dim)", textTransform: "capitalize" }}>{t}</span>
                <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text-primary)" }}>
                  {(d.by_type || {})[t] || 0}
                </span>
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, accent, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-md p-3 text-left transition-transform"
    style={{
      background: "var(--bg-surface)",
      border: "1px solid var(--border-strong)",
      borderLeft: `3px solid ${accent}`,
      cursor: onClick ? "pointer" : "default",
      width: "100%",
    }}
    onMouseEnter={(e) => { if (onClick) e.currentTarget.style.transform = "translateY(-1px)"; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
    data-testid={testid}
  >
    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "var(--text-dimmer)", letterSpacing: "1.5px", textTransform: "uppercase" }}>{label}</div>
    <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 28, color: accent, marginTop: 4 }}>{value}</div>
  </button>
);

const Card = ({ title, children }) => (
  <div className="rounded-md p-3" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-strong)" }}>
    <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, color: "var(--text-dim)", marginBottom: 8, letterSpacing: "0.3px" }}>{title}</div>
    {children}
  </div>
);

// ───────────────────────────── list ─────────────────────────────

const ListTab = ({ filters, setFilters, onSelect }) => {
  const q = useLogisticsRequests(filters);
  const rows = q.data?.items || [];
  const activeFilterChips = [];
  if (filters.assignee_role === "pricer") activeFilterChips.push({ k: "assignee_role", label: "Pricer (me)" });
  if (filters.assignee_role === "handler") activeFilterChips.push({ k: "assignee_role", label: "Handler (me)" });
  if (filters.due === "today") activeFilterChips.push({ k: "due", label: "Due today" });
  if (filters.due === "week") activeFilterChips.push({ k: "due", label: "Due this week" });

  return (
    <div className="space-y-3" data-testid="logistics-list">
      <div className="flex flex-wrap gap-2">
        <SelectChip label="Stage" value={filters.stage} onChange={(v) => setFilters((f) => ({ ...f, stage: v }))} options={[["", "All"], ...LR_STAGES.map((s) => [s, STAGE_LABELS[s]])]} testid="logistics-filter-stage" />
        <SelectChip label="Type" value={filters.request_type} onChange={(v) => setFilters((f) => ({ ...f, request_type: v }))} options={[["", "All"], ...LR_TYPES.map((t) => [t, t])]} testid="logistics-filter-type" />
        <TextChip label="Customer" value={filters.customer} onChange={(v) => setFilters((f) => ({ ...f, customer: v }))} testid="logistics-filter-customer" />
        <label className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-strong)", fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)", cursor: "pointer" }}>
          <input type="checkbox" checked={filters.mine} onChange={(e) => setFilters((f) => ({ ...f, mine: e.target.checked }))} data-testid="logistics-filter-mine" />
          Mine only
        </label>
        {activeFilterChips.map((c) => (
          <button key={c.k} type="button"
            onClick={() => setFilters((f) => ({ ...f, [c.k]: "" }))}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded"
            style={{ background: "rgba(24,95,165,0.18)", border: "1px solid #185FA5", color: "#185FA5", fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 700, cursor: "pointer" }}
            data-testid={`logistics-active-filter-${c.k}`}>
            {c.label} <X size={11} />
          </button>
        ))}
      </div>
      <div className="rounded-md overflow-hidden" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-strong)" }}>
        <div className="grid items-center px-3 py-2" style={{ gridTemplateColumns: "1.5fr 0.6fr 0.85fr 0.95fr 1.15fr 0.95fr 0.85fr 0.65fr", background: "var(--bg-elevated)", borderBottom: "1px solid var(--border-strong)" }}>
          {["Deal", "Type", "Stage", "Contract", "Customer", "Amount", "Leadási hat.", "Created"].map((h) => (
            <div key={h} style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "var(--text-dimmer)", letterSpacing: "1.5px", textTransform: "uppercase" }}>{h}</div>
          ))}
        </div>
        {q.isLoading && <Empty>Loading…</Empty>}
        {!q.isLoading && rows.length === 0 && <Empty>No requests match the current filters.</Empty>}
        {rows.map((r) => (
          <div key={r.id} onClick={() => onSelect(r)} className="grid items-center px-3 py-2 cursor-pointer" style={{ gridTemplateColumns: "1.5fr 0.6fr 0.85fr 0.95fr 1.15fr 0.95fr 0.85fr 0.65fr", borderBottom: "1px solid var(--border-soft)" }} data-testid={`logistics-row-${r.id}`}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(24,95,165,0.08)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <div>
              <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, color: "var(--text-primary)" }}>{r.deal_name}</div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "var(--text-dimmer)" }}>{r.id}</div>
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dim)", textTransform: "capitalize" }}>{r.request_type}</div>
            <StageBadge stage={r.deal_stage} />
            <ContractPill row={r} />
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dim)" }}>{r.customer_name || "—"}</div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--green)", fontWeight: 700 }}>{fmtMoney(r.amount || r.elfogadott_dij || r.celar, r.currency)}</div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dim)" }}>{fmtDate(r.leadasi_hatarido)}</div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "var(--text-dimmer)" }}>{fmtDate(r.created_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const StageBadge = ({ stage }) => (
  <span className="inline-block px-2 py-0.5 rounded" style={{ background: `${STAGE_COLORS[stage] || "#9198b0"}22`, border: `1px solid ${STAGE_COLORS[stage] || "#9198b0"}77`, color: STAGE_COLORS[stage] || "#9198b0", fontFamily: "'DM Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase" }}>
    {STAGE_LABELS[stage] || stage}
  </span>
);

// Phase J.3 — Contract / Spot / Deviation-Pending pill.
//   • CONTRACT          — deal has a contract_rate_id and is not waiting on a deviation gate.
//   • DEVIATION PENDING — deal has a contract_rate_id but deal_stage is waiting_for_approval.
//   • SPOT              — no contract_rate_id (priced ad-hoc).
const ContractPill = ({ row }) => {
  let label = "SPOT";
  let color = "#9198b0";
  let testid = `logistics-contract-pill-spot-${row.id}`;
  if (row.contract_rate_id) {
    if (row.deal_stage === "waiting_for_approval" || row.is_contract_deviation) {
      label = "DEVIATION PENDING";
      color = "#e6a700";
      testid = `logistics-contract-pill-deviation-${row.id}`;
    } else {
      label = "CONTRACT";
      color = "#0d6b39";
      testid = `logistics-contract-pill-contract-${row.id}`;
    }
  }
  return (
    <span
      data-testid={testid}
      className="inline-block px-2 py-0.5 rounded"
      style={{
        background: `${color}22`,
        border: `1px solid ${color}77`,
        color,
        fontFamily: "'DM Mono', monospace",
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

const Empty = ({ children }) => (
  <div className="px-3 py-6 text-center" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text-dimmer)" }}>{children}</div>
);

const SelectChip = ({ label, value, onChange, options, testid }) => (
  <label className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-strong)", fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text-dimmer)" }}>
    <span>{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="bg-transparent outline-none" style={{ color: "var(--text-primary)" }} data-testid={testid}>
      {options.map(([v, l]) => <option key={v} value={v} style={{ background: "var(--bg-surface)", color: "var(--text-primary)" }}>{l}</option>)}
    </select>
  </label>
);

const TextChip = ({ label, value, onChange, testid }) => (
  <label className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-strong)", fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text-dimmer)" }}>
    <span>{label}</span>
    <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className="bg-transparent outline-none" style={{ color: "var(--text-primary)", width: 140 }} data-testid={testid} placeholder="filter…" />
  </label>
);

// ───────────────────────────── calendar ─────────────────────────────

const CalendarTab = ({ onSelect }) => {
  // Iter85b A2 — horizontally scrollable, with a wide range so users can scroll
  // sideways through months. Default range: previous month → +5 months ahead
  // (7 month-cards). Prev/Next buttons nudge the scrollbar horizontally.
  const today = new Date();
  const startMonth = useMemo(() => new Date(today.getFullYear(), today.getMonth() - 1, 1), []);
  const endMonth = useMemo(() => new Date(today.getFullYear(), today.getMonth() + 6, 0), []);
  const since = startMonth.toISOString().slice(0, 10);
  const until = endMonth.toISOString().slice(0, 10);
  const q = useLogisticsCalendar(since, until);
  const events = q.data?.events || [];
  const byDate = useMemo(() => {
    const m = {};
    events.forEach((e) => { (m[e.date] = m[e.date] || []).push(e); });
    return m;
  }, [events]);
  const scrollerRef = React.useRef(null);
  const monthsToRender = [];
  for (let off = -1; off <= 5; off++) monthsToRender.push(off);
  const nudge = (px) => { if (scrollerRef.current) scrollerRef.current.scrollBy({ left: px, behavior: "smooth" }); };

  return (
    <div className="space-y-2" data-testid="logistics-calendar">
      <div className="flex items-center justify-between">
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)", letterSpacing: "1.5px", textTransform: "uppercase" }}>
          Scroll sideways through months · {events.length} events
        </div>
        <div className="flex gap-1">
          <button type="button" onClick={() => nudge(-340)} className="inline-flex items-center gap-1 px-2 py-1 rounded" style={navBtn} data-testid="logistics-calendar-prev">
            <ChevronLeft size={13} /> Prev
          </button>
          <button type="button" onClick={() => nudge(340)} className="inline-flex items-center gap-1 px-2 py-1 rounded" style={navBtn} data-testid="logistics-calendar-next">
            Next <ChevronRight size={13} />
          </button>
        </div>
      </div>
      <div
        ref={scrollerRef}
        className="flex gap-3 overflow-x-auto pb-2"
        style={{ scrollSnapType: "x mandatory", scrollbarWidth: "thin" }}
        data-testid="logistics-calendar-scroller"
      >
        {monthsToRender.map((offset) => (
          <MonthBlock key={offset} year={today.getFullYear()} month={today.getMonth() + offset} byDate={byDate} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
};

const navBtn = {
  background: "var(--bg-surface)",
  border: "1px solid #185FA5",
  color: "#185FA5",
  fontFamily: "'DM Mono', monospace",
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.5px",
  cursor: "pointer",
};

const MonthBlock = ({ year, month, byDate, onSelect }) => {
  const date = new Date(year, month, 1);
  const monthLabel = date.toLocaleString(undefined, { month: "long", year: "numeric" });
  const firstDow = (date.getDay() + 6) % 7; // Monday-start
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;
  return (
    <div
      className="rounded p-3 flex-shrink-0"
      style={{
        background: "var(--bg-surface)",
        border: `1px solid ${isCurrentMonth ? "#185FA5" : "var(--border-strong)"}`,
        width: 340,
        scrollSnapAlign: "start",
      }}
      data-testid={`logistics-calendar-month-${year}-${String(month + 1).padStart(2, "0")}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>{monthLabel}</div>
        {isCurrentMonth && <span data-testid="logistics-calendar-now-badge" style={{ fontFamily: "'DM Mono', monospace", fontSize: 8, color: "#185FA5", fontWeight: 700, letterSpacing: "1px" }}>NOW</span>}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
          <div key={d} className="text-center" style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "var(--text-dimmer)", letterSpacing: "1px" }}>{d}</div>
        ))}
        {cells.map((d, i) => {
          if (d == null) return <div key={`e-${i}`} />;
          const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const evs = byDate[iso] || [];
          const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;
          return (
            <div key={iso} className="rounded p-1" style={{ minHeight: 56, background: "var(--bg-elevated)", border: `1px solid ${isToday ? "#185FA5" : "var(--border-soft)"}` }} data-testid={isToday ? "logistics-calendar-today" : undefined}>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, fontWeight: isToday ? 700 : 400, color: isToday ? "#185FA5" : (evs.length ? "var(--text-primary)" : "var(--text-dimmer)") }}>{d}</div>
              {evs.slice(0, 2).map((e, idx) => (
                <div key={idx} onClick={() => onSelect(e.request_id)} className="cursor-pointer truncate" style={{ fontFamily: "'DM Mono', monospace", fontSize: 8.5, color: STAGE_COLORS[e.deal_stage] || "var(--text-dim)", marginTop: 1, fontWeight: 600 }} title={`${e.label}: ${e.deal_name}`} data-testid={`logistics-calendar-event-${e.request_id}-${e.field}`}>
                  · {e.deal_name}
                </div>
              ))}
              {evs.length > 2 && <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 8, color: "var(--text-dimmer)" }}>+{evs.length - 2}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ───────────────────────────── create modal ─────────────────────────────

const CreateLogisticsModal = ({ onClose, onCreated }) => {
  const cats = useLogisticsCategories();
  const create = useCreateLogistics();
  const newCat = useCreateLogisticsCategory();
  const [form, setForm] = useState({
    deal_name: "", request_type: "tender", customer_name: "", customer_category_id: "",
    is_tender: true, currency: "EUR", celar: "", leadasi_hatarido: "",
    felrako_orszagok: "", lerako_orszagok: "", suly_kg: "", rakmeter: "", kobmeter: "",
    aru_megnevezese: "", autotipus: "",
    pickup1_city: "", pickup1_country: "", pickup1_date: "",
    dropoff1_city: "", dropoff1_country: "", dropoff1_date: "",
    szerzodes_idotartama_months: "", kovetkezo_tender_date: "",
    kiegeszito_info: "",
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const [err, setErr] = useState("");
  const [newCatName, setNewCatName] = useState("");
  const submit = () => {
    setErr("");
    const body = {
      deal_name: form.deal_name.trim(),
      request_type: form.request_type,
      customer_name: form.customer_name.trim() || null,
      customer_category_id: form.customer_category_id || null,
      is_tender: form.request_type === "tender" ? true : !!form.is_tender,
      currency: form.currency,
      celar: form.celar === "" ? null : Number(form.celar),
      leadasi_hatarido: form.leadasi_hatarido || null,
      felrako_orszagok: (form.felrako_orszagok || "").split(",").map((s) => s.trim()).filter(Boolean),
      lerako_orszagok: (form.lerako_orszagok || "").split(",").map((s) => s.trim()).filter(Boolean),
      suly_kg: form.suly_kg === "" ? null : Number(form.suly_kg),
      rakmeter: form.rakmeter === "" ? null : Number(form.rakmeter),
      kobmeter: form.kobmeter === "" ? null : Number(form.kobmeter),
      aru_megnevezese: form.aru_megnevezese.trim() || null,
      autotipus: form.autotipus.trim() || null,
      pickups: form.pickup1_city || form.pickup1_country || form.pickup1_date ? [{
        date_time: form.pickup1_date || null, city: form.pickup1_city || null, country: form.pickup1_country || null,
      }] : [],
      dropoffs: form.dropoff1_city || form.dropoff1_country || form.dropoff1_date ? [{
        date_time: form.dropoff1_date || null, city: form.dropoff1_city || null, country: form.dropoff1_country || null,
      }] : [],
      szerzodes_idotartama_months: form.szerzodes_idotartama_months === "" ? null : parseInt(form.szerzodes_idotartama_months, 10),
      kovetkezo_tender_date: form.kovetkezo_tender_date || null,
      kiegeszito_info: form.kiegeszito_info.trim() || null,
    };
    if (!body.deal_name) { setErr("Deal name is required"); return; }
    create.mutate(body, {
      onSuccess: (d) => onCreated(d),
      onError: (e) => setErr(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Create failed"),
    });
  };

  return (
    <>
      <div className="fixed inset-0 z-[9990]" style={{ background: "rgba(8,9,13,0.65)", backdropFilter: "blur(4px)" }} onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 z-[9991] -translate-x-1/2 -translate-y-1/2 rounded-lg overflow-y-auto" style={{ width: "min(800px, 96vw)", maxHeight: "92vh", background: "#0d0f18", border: "1px solid rgba(255,255,255,0.10)" }} data-testid="logistics-create-modal">
        <div className="px-5 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 16, color: "#f0f2f8" }}>New logistics request</div>
          <button onClick={onClose} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.10)", color: "#c5cad8", borderRadius: 4, padding: "4px 6px", cursor: "pointer" }}><X size={13} /></button>
        </div>
        <div className="p-5 space-y-4">
          <Section title="Identification">
            <Field label="Deal name *">
              <Input value={form.deal_name} onChange={(v) => set("deal_name", v)} testid="lr-deal-name" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Request type *">
                <Select value={form.request_type} onChange={(v) => set("request_type", v)} options={LR_TYPES.map((t) => [t, t])} testid="lr-request-type" />
              </Field>
              <Field label="Currency">
                <Select value={form.currency} onChange={(v) => set("currency", v)} options={[["EUR", "EUR"], ["USD", "USD"], ["HUF", "HUF"], ["GBP", "GBP"]]} testid="lr-currency" />
              </Field>
            </div>
          </Section>
          <Section title="Customer">
            <Field label="Customer name">
              <Input value={form.customer_name} onChange={(v) => set("customer_name", v)} testid="lr-customer-name" />
            </Field>
            <div className="flex gap-2 items-end">
              <Field label="Customer category" className="flex-1">
                <Select value={form.customer_category_id} onChange={(v) => set("customer_category_id", v)} options={[["", "—"], ...((cats.data?.items || []).map((c) => [c.id, c.name]))]} testid="lr-customer-category" />
              </Field>
              <div className="flex gap-1">
                <Input value={newCatName} onChange={setNewCatName} placeholder="+ new" testid="lr-new-category-name" style={{ width: 100 }} />
                <button type="button" onClick={() => { if (newCatName.trim()) newCat.mutate({ name: newCatName.trim() }, { onSuccess: () => setNewCatName("") }); }} style={btn("blue")} data-testid="lr-new-category-add" className="px-2 py-1 rounded">+</button>
              </div>
            </div>
          </Section>
          <Section title="Route (first pickup + dropoff in 1A)">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Felrakó országok (CSV)"><Input value={form.felrako_orszagok} onChange={(v) => set("felrako_orszagok", v)} placeholder="DE, AT" testid="lr-pickup-countries" /></Field>
              <Field label="Lerakó országok (CSV)"><Input value={form.lerako_orszagok} onChange={(v) => set("lerako_orszagok", v)} placeholder="BE, NL" testid="lr-dropoff-countries" /></Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Pickup city"><Input value={form.pickup1_city} onChange={(v) => set("pickup1_city", v)} testid="lr-pickup1-city" /></Field>
              <Field label="Pickup country"><Input value={form.pickup1_country} onChange={(v) => set("pickup1_country", v)} testid="lr-pickup1-country" /></Field>
              <Field label="Pickup date/time"><Input type="datetime-local" value={form.pickup1_date} onChange={(v) => set("pickup1_date", v)} testid="lr-pickup1-date" /></Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Dropoff city"><Input value={form.dropoff1_city} onChange={(v) => set("dropoff1_city", v)} testid="lr-dropoff1-city" /></Field>
              <Field label="Dropoff country"><Input value={form.dropoff1_country} onChange={(v) => set("dropoff1_country", v)} testid="lr-dropoff1-country" /></Field>
              <Field label="Dropoff date/time"><Input type="datetime-local" value={form.dropoff1_date} onChange={(v) => set("dropoff1_date", v)} testid="lr-dropoff1-date" /></Field>
            </div>
          </Section>
          <Section title="Cargo">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Áru megnevezése"><Input value={form.aru_megnevezese} onChange={(v) => set("aru_megnevezese", v)} testid="lr-aru" /></Field>
              <Field label="Autótípus"><Input value={form.autotipus} onChange={(v) => set("autotipus", v)} testid="lr-autotipus" /></Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Súly (kg)"><Input type="number" value={form.suly_kg} onChange={(v) => set("suly_kg", v)} testid="lr-weight" /></Field>
              <Field label="Rakméter (LDM)"><Input type="number" value={form.rakmeter} onChange={(v) => set("rakmeter", v)} testid="lr-ldm" /></Field>
              <Field label="Köbméter"><Input type="number" value={form.kobmeter} onChange={(v) => set("kobmeter", v)} testid="lr-volume" /></Field>
            </div>
          </Section>
          <Section title="Pricing + dates">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Célár"><Input type="number" value={form.celar} onChange={(v) => set("celar", v)} testid="lr-celar" /></Field>
              <Field label="Leadási határidő"><Input type="date" value={form.leadasi_hatarido} onChange={(v) => set("leadasi_hatarido", v)} testid="lr-leadasi-hatarido" /></Field>
            </div>
          </Section>
          {form.request_type === "tender" && (
            <Section title="Tender">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Szerződés időtartama (hó)"><Input type="number" value={form.szerzodes_idotartama_months} onChange={(v) => set("szerzodes_idotartama_months", v)} testid="lr-szerzodes" /></Field>
                <Field label="Következő tender (date)"><Input type="date" value={form.kovetkezo_tender_date} onChange={(v) => set("kovetkezo_tender_date", v)} testid="lr-kovetkezo-tender" /></Field>
              </div>
            </Section>
          )}
          <Section title="Notes">
            <Field label="Kiegészítő információk"><Input value={form.kiegeszito_info} onChange={(v) => set("kiegeszito_info", v)} testid="lr-notes" /></Field>
          </Section>
          {err && <div className="px-3 py-2 rounded" style={{ background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.35)", color: "#ff9a99", fontFamily: "'DM Mono', monospace", fontSize: 11 }} data-testid="lr-create-error">{err}</div>}
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-2 rounded" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.10)", color: "#c5cad8", fontFamily: "'DM Mono', monospace", fontSize: 11, cursor: "pointer" }} data-testid="lr-create-cancel">Cancel</button>
            <button onClick={submit} disabled={create.isPending} className="px-3 py-2 rounded" style={{ ...btn("green"), opacity: create.isPending ? 0.6 : 1 }} data-testid="lr-create-submit">
              {create.isPending ? "Saving…" : "Create"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const Section = ({ title, children }) => (
  <div>
    <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 11, color: "#9ec3ec", marginBottom: 6, letterSpacing: "0.5px", textTransform: "uppercase" }}>{title}</div>
    <div className="space-y-2">{children}</div>
  </div>
);

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0", letterSpacing: "0.5px", marginBottom: 2 }}>{label}</div>
    {children}
  </label>
);

const Input = ({ value, onChange, type = "text", testid, placeholder, style }) => (
  <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full px-2 py-1.5 rounded outline-none" style={{ background: "#08090d", border: "1px solid rgba(255,255,255,0.10)", color: "#f0f2f8", fontFamily: "'DM Mono', monospace", fontSize: 11, ...(style || {}) }} data-testid={testid} />
);

const Select = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-2 py-1.5 rounded outline-none" style={{ background: "#08090d", border: "1px solid rgba(255,255,255,0.10)", color: "#f0f2f8", fontFamily: "'DM Mono', monospace", fontSize: 11 }} data-testid={testid}>
    {options.map(([v, l]) => <option key={v} value={v} style={{ background: "#0d0f18" }}>{l}</option>)}
  </select>
);

// ───────────────────────────── detail drawer ─────────────────────────────

const useRequest = (id) => {
  const q = useLogisticsRequests({});
  return q.data?.items?.find((r) => r.id === id);
};

const DetailDrawer = ({ id, onClose }) => {
  const r = useRequest(id);
  const tx = useTransitionLogistics();
  const patch = usePatchLogistics();
  const users = useAdminUsers();
  const { user: me } = useAuth();
  const [pickStage, setPickStage] = useState("");
  const [closedReason, setClosedReason] = useState("");
  const [amount, setAmount] = useState("");
  const [elfDij, setElfDij] = useState("");
  const [err, setErr] = useState("");
  if (!r) return null;
  const allowedNext = ALLOWED_TRANSITIONS[r.deal_stage] || [];

  const doTx = (stage) => {
    setErr("");
    const body = { stage };
    if (stage === "assigned" && !r.arazo_user_id) body.arazo_user_id = me?.id;
    if (stage === "priced" && !r.elfogadott_dij) body.elfogadott_dij = Number(elfDij) || 0;
    if (stage === "won" || stage === "lost") {
      body.closed_reason = closedReason || (stage === "won" ? "Customer accepted" : "Customer declined");
      if (stage === "won") body.amount = Number(amount) || r.celar || 0;
    }
    tx.mutate({ id: r.id, body }, {
      onError: (e) => {
        const detail = e?.response?.data?.detail;
        if (typeof detail === "object" && detail.missing) {
          setErr(`Missing required fields: ${detail.missing.join(", ")}`);
        } else {
          setErr(detail || "Transition failed");
        }
      },
    });
  };

  return (
    <>
      <div className="fixed inset-0 z-[9990]" style={{ background: "rgba(8,9,13,0.55)" }} onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 z-[9991] overflow-y-auto" style={{ width: "min(680px, 100vw)", background: "#0a0c14", borderLeft: "1px solid rgba(255,255,255,0.08)" }} data-testid="logistics-detail-drawer">
        <div className="px-5 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16, color: "#f0f2f8" }}>{r.deal_name}</div>
              <div className="flex items-center gap-2 mt-1">
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0" }}>{r.id}</span>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#c5cad8", textTransform: "capitalize" }}>· {r.request_type}</span>
                <StageBadge stage={r.deal_stage} />
              </div>
            </div>
            <div className="flex gap-1">
              <button type="button" onClick={() => exportLogisticsPdf(r.id)} className="px-2 py-1 rounded inline-flex items-center gap-1" style={btn("blue")} data-testid="logistics-export-pdf"><FileText size={11} /> PDF</button>
              <button onClick={onClose} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.10)", color: "#c5cad8", borderRadius: 4, padding: "4px 6px", cursor: "pointer" }} data-testid="logistics-detail-close"><X size={13} /></button>
            </div>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <KvBlock title="Customer" rows={[["Name", r.customer_name], ["Category", r.customer_category_id], ["Email", r.customer_email], ["Phone", r.customer_phone]]} />
          <KvBlock title="Route" rows={[
            ["Felrakó országok", (r.felrako_orszagok || []).join(", ")],
            ["Lerakó országok", (r.lerako_orszagok || []).join(", ")],
            ...((r.pickups || []).map((p, i) => [`Pickup #${i + 1}`, `${(p.date_time || "").slice(0, 16)} · ${p.city || ""} ${p.country || ""}`])),
            ...((r.dropoffs || []).map((p, i) => [`Dropoff #${i + 1}`, `${(p.date_time || "").slice(0, 16)} · ${p.city || ""} ${p.country || ""}`])),
            ["Körfuvar", r.korfuvar ? "Yes" : "No"],
          ]} />
          <KvBlock title="Cargo" rows={[
            ["Áru", r.aru_megnevezese], ["Autótípus", r.autotipus],
            ["Súly (kg)", r.suly_kg], ["LDM", r.rakmeter], ["Köbméter", r.kobmeter],
            ["ADR", r.adr ? "Yes" : "No"], ["UN szám", r.un_szam],
          ]} />
          <KvBlock title="Pricing" rows={[
            ["Currency", r.currency], ["Amount", r.amount],
            ["Célár", r.celar], ["Elfogadott díj", r.elfogadott_dij],
            ["Konkurencia díj", r.konkurencia_dij],
          ]} />
          <KvBlock title="Dates" rows={[
            ["Árazásra kiküldve", fmtDate(r.arazasra_kikuldve_at)],
            ["Leadási határidő", fmtDate(r.leadasi_hatarido)],
            ["Kezdési dátum", fmtDate(r.kezdesi_datum)],
            ["Projekt kezdete", fmtDate(r.projekt_kezdete)],
          ]} />
          {r.is_tender && (
            <KvBlock title="Tender" rows={[
              ["Szerződés (hó)", r.szerzodes_idotartama_months],
              ["Átfutási idő (nap)", r.tender_atfutasi_napok],
              ["Következő tender", fmtDate(r.kovetkezo_tender_date)],
            ]} />
          )}
          {r.kivonat && Object.keys(r.kivonat).length > 0 && (
            <KvBlock title="Tender Kivonat" rows={Object.entries(r.kivonat).map(([k, v]) => [k, v])} testid="logistics-kivonat-block" />
          )}
          <KvBlock title="Notes" rows={[
            ["Kiegészítő info", r.kiegeszito_info],
            ["Szakmai kifogás", r.szakmai_kifogas],
            ["Closed reason", r.closed_reason],
          ]} />
          {/* Workflow controls */}
          <div className="rounded p-3" style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="logistics-detail-workflow">
            <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, color: "#9ec3ec", marginBottom: 8, letterSpacing: "0.5px", textTransform: "uppercase" }}>Workflow</div>
            {allowedNext.length === 0 ? <Empty>Terminal stage.</Empty> : (
              <>
                <div className="flex flex-wrap gap-2 mb-2">
                  {allowedNext.map((s) => (
                    <button key={s} type="button" onClick={() => doTx(s)} className="px-3 py-1.5 rounded" style={{ background: `${STAGE_COLORS[s]}22`, border: `1px solid ${STAGE_COLORS[s]}66`, color: STAGE_COLORS[s], fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", cursor: "pointer" }} data-testid={`logistics-transition-${s}`}>
                      → {STAGE_LABELS[s]}
                    </button>
                  ))}
                </div>
                {(allowedNext.includes("priced") && !r.elfogadott_dij) && (
                  <Field label="Elfogadott díj (required for `priced`)"><Input type="number" value={elfDij} onChange={setElfDij} testid="logistics-tx-elfdij" /></Field>
                )}
                {(allowedNext.includes("won") || allowedNext.includes("lost")) && (
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Closed reason"><Input value={closedReason} onChange={setClosedReason} testid="logistics-tx-reason" /></Field>
                    <Field label="Amount (for won)"><Input type="number" value={amount} onChange={setAmount} testid="logistics-tx-amount" /></Field>
                  </div>
                )}
                {err && <div className="mt-2 px-2 py-1 rounded" style={{ background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.35)", color: "#ff9a99", fontFamily: "'DM Mono', monospace", fontSize: 10 }} data-testid="logistics-tx-error">{err}</div>}
              </>
            )}
          </div>
          {/* Assign quick */}
          <div className="rounded p-3" style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, color: "#9ec3ec", marginBottom: 8, letterSpacing: "0.5px", textTransform: "uppercase" }}>Assignment</div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Árazó (Pricer)">
                <Select value={r.arazo_user_id || ""} onChange={(v) => patch.mutate({ id: r.id, body: { arazo_user_id: v || null } })} options={[["", "—"], ...((users.data || []).map((u) => [u.id, `${u.name || u.email}`]))]} testid="logistics-assign-arazo" />
              </Field>
              <Field label="Ügyfélszolgálat (Handler)">
                <Select value={r.ugyfelszolgalat_user_id || ""} onChange={(v) => patch.mutate({ id: r.id, body: { ugyfelszolgalat_user_id: v || null } })} options={[["", "—"], ...((users.data || []).map((u) => [u.id, `${u.name || u.email}`]))]} testid="logistics-assign-handler" />
              </Field>
            </div>
            <div className="mt-1">
              <button type="button" onClick={() => patch.mutate({ id: r.id, body: { arazo_user_id: me?.id } })} className="px-2 py-1 rounded" style={btn("blue")} data-testid="logistics-assign-self">Assign myself as Pricer</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const KvBlock = ({ title, rows, testid }) => {
  const filtered = rows.filter(([, v]) => v != null && v !== "" && !(Array.isArray(v) && v.length === 0));
  if (filtered.length === 0) return null;
  return (
    <div className="rounded p-3" style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={testid}>
      <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, color: "#9ec3ec", marginBottom: 8, letterSpacing: "0.5px", textTransform: "uppercase" }}>{title}</div>
      <div className="space-y-1.5">
        {filtered.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-3">
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0", letterSpacing: "0.3px" }}>{k}</span>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#f0f2f8", textAlign: "right" }}>{String(v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Mirror of backend ALLOWED_TRANSITIONS — duplicated to avoid an extra API call.
const ALLOWED_TRANSITIONS = {
  registered:           ["assigned", "cancelled", "rejected_by_provider"],
  assigned:             ["waiting_for_pricing", "cancelled", "registered", "rejected_by_provider"],
  waiting_for_pricing:  ["priced", "cancelled", "assigned", "rejected_by_provider"],
  priced:               ["waiting_for_approval", "to_send", "cancelled", "waiting_for_pricing", "rejected_by_provider"],
  waiting_for_approval: ["to_send", "cancelled", "priced", "rejected_by_provider"],
  to_send:              ["offered", "lost", "cancelled", "priced", "rejected_by_provider"],
  offered:              ["won", "lost", "cancelled", "to_send", "rejected_by_provider"],
  won:                  ["executed"],
  executed:             [],
  lost:                 ["registered"],
  cancelled:            ["registered"],
  rejected_by_provider: ["registered"],
};


// ───────────────────────────── Statistics (iter86b B7) ─────────────────────────────


// ────────────────────── Tender Schedule (γ-3-C) ──────────────────────
// Past tenders + computed upcoming entries from `kovetkezo_tender_date`.
// Each upcoming entry exposes T-3mo (calendar marker) + T-1mo (notification trigger).

const TenderScheduleTab = ({ onSelect }) => {
  const q = useTenderSchedule(6);
  if (q.isLoading) return <div className="px-4 py-6" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}>Loading…</div>;
  const past = q.data?.past || [];
  const upcoming = q.data?.upcoming || [];
  return (
    <div className="space-y-6" data-testid="logistics-tender-schedule">
      <div className="rounded-md p-4" style={{ background: "var(--bg-surface, #fff)", border: "1px solid var(--border-strong, rgba(0,0,0,0.10))" }}>
        <div className="flex items-center justify-between mb-3">
          <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14 }}>Upcoming tenders ({upcoming.length})</span>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#6b7388", letterSpacing: "1px", textTransform: "uppercase" }}>Horizon: {q.data?.horizon_months || 6} months</span>
        </div>
        {upcoming.length === 0 ? (
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#6b7388" }}>No upcoming tenders scheduled.</div>
        ) : (
          <div className="grid gap-2">
            {upcoming.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => onSelect && onSelect(u.id)}
                className="text-left rounded-md p-3"
                style={{ background: "var(--bg, #f6f7fb)", border: "1px solid var(--border-strong, rgba(0,0,0,0.08))", borderLeft: "3px solid #185FA5", cursor: "pointer" }}
                data-testid={`tender-upcoming-${u.id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13 }}>{u.deal_name || u.id}</span>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#185FA5", padding: "1px 6px", background: "rgba(24,95,165,0.10)", borderRadius: 3, fontWeight: 700 }}>
                    {u.expected_date}
                  </span>
                </div>
                {u.customer_name && (
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#6b7388", marginTop: 2 }}>{u.customer_name}</div>
                )}
                <div className="flex gap-2 mt-2 flex-wrap" style={{ fontFamily: "'DM Mono', monospace", fontSize: 9 }}>
                  <span style={{ color: "#7e3aa8", padding: "1px 5px", background: "rgba(126,58,168,0.10)", borderRadius: 3, fontWeight: 700 }}>T-3mo calendar: {u.t_minus_3_date}</span>
                  <span style={{ color: "#b7770d", padding: "1px 5px", background: "rgba(183,119,13,0.10)", borderRadius: 3, fontWeight: 700 }}>T-1mo reminder: {u.t_minus_1_date}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-md p-4" style={{ background: "var(--bg-surface, #fff)", border: "1px solid var(--border-strong, rgba(0,0,0,0.10))" }}>
        <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Past tenders ({past.length})</div>
        {past.length === 0 ? (
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#6b7388" }}>No past tender schedule entries.</div>
        ) : (
          <div className="grid gap-1">
            {past.slice(0, 30).map((p) => (
              <button key={p.id} type="button" onClick={() => onSelect && onSelect(p.id)} className="text-left px-2 py-1.5 rounded" style={{ background: "transparent", border: "none", borderBottom: "1px dashed rgba(0,0,0,0.06)", fontFamily: "'DM Mono', monospace", fontSize: 11, cursor: "pointer" }} data-testid={`tender-past-${p.id}`}>
                <span style={{ color: "#6b7388" }}>{p.expected_date}</span> · <strong>{p.deal_name || p.id}</strong>{p.customer_name ? ` · ${p.customer_name}` : ""}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


const StatsTab = () => {
  const [filters, setFilters] = useState({ since: "", until: "", customer: "", customer_category_id: "", request_type: "" });
  const cats = useLogisticsCategories();
  const q = useLogisticsStats(filters);
  const d = q.data || { totals: {}, by_period: [], by_customer_category: [], by_customer: [], by_request_type: [] };
  const CHART_COLORS = ["#185FA5", "#22a35a", "#b7770d", "#7e3aa8", "#b22a29", "#0d6b39", "#5a7adb", "#c45c2a"];
  return (
    <div className="space-y-4" data-testid="logistics-stats">
      <div className="flex flex-wrap gap-2">
        <TextChip label="Customer" value={filters.customer} onChange={(v) => setFilters((f) => ({ ...f, customer: v }))} testid="stats-filter-customer" />
        <SelectChip label="Category" value={filters.customer_category_id} onChange={(v) => setFilters((f) => ({ ...f, customer_category_id: v }))} options={[["", "All"], ...((cats.data?.items || []).map((c) => [c.id, c.name]))]} testid="stats-filter-category" />
        <SelectChip label="Type" value={filters.request_type} onChange={(v) => setFilters((f) => ({ ...f, request_type: v }))} options={[["", "All"], ...LR_TYPES.map((t) => [t, t])]} testid="stats-filter-type" />
        <label className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-strong)", fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)" }}>
          <span>From</span>
          <input type="date" value={filters.since} onChange={(e) => setFilters((f) => ({ ...f, since: e.target.value }))} className="bg-transparent outline-none" style={{ color: "var(--text-primary)" }} data-testid="stats-filter-since" />
        </label>
        <label className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-strong)", fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)" }}>
          <span>To</span>
          <input type="date" value={filters.until} onChange={(e) => setFilters((f) => ({ ...f, until: e.target.value }))} className="bg-transparent outline-none" style={{ color: "var(--text-primary)" }} data-testid="stats-filter-until" />
        </label>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
        <KpiTile label="Total deals" value={d.totals.total_count ?? 0} accent="#185FA5" testid="stats-kpi-total" />
        <KpiTile label="Won" value={d.totals.won_count ?? 0} accent="#22a35a" testid="stats-kpi-won" />
        <KpiTile label="Lost" value={d.totals.lost_count ?? 0} accent="#b22a29" testid="stats-kpi-lost" />
        <KpiTile label="Win rate" value={`${d.totals.win_rate_pct ?? 0}%`} accent="#b7770d" testid="stats-kpi-winrate" />
        <KpiTile
          label="Vehicle delays"
          value={`${d.totals.vehicle_delay_count ?? 0} · ${d.totals.vehicle_delay_pct ?? 0}%`}
          accent={d.totals.vehicle_delay_pct >= 5 ? "#b22a29" : "#6b7388"}
          testid="stats-kpi-vehicle-delays"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="By month">
          <div style={{ width: "100%", height: 240 }} data-testid="stats-chart-by-period">
            <ResponsiveContainer>
              <LineChart data={d.by_period}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-soft)" />
                <XAxis dataKey="bucket" stroke="var(--text-dim)" fontSize={10} />
                <YAxis stroke="var(--text-dim)" fontSize={10} />
                <Tooltip contentStyle={{ background: "var(--bg-surface)", border: "1px solid var(--border-strong)", fontFamily: "'DM Mono', monospace", fontSize: 11 }} />
                <Line type="monotone" dataKey="count" stroke="#185FA5" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="By request type">
          <div style={{ width: "100%", height: 240 }} data-testid="stats-chart-by-type">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={d.by_request_type} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={80}>
                  {d.by_request_type.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "var(--bg-surface)", border: "1px solid var(--border-strong)", fontFamily: "'DM Mono', monospace", fontSize: 11 }} />
                <Legend wrapperStyle={{ fontFamily: "'DM Mono', monospace", fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="By customer category">
          <div style={{ width: "100%", height: 240 }} data-testid="stats-chart-by-cat">
            <ResponsiveContainer>
              <BarChart data={d.by_customer_category}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-soft)" />
                <XAxis dataKey="name" stroke="var(--text-dim)" fontSize={9} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis stroke="var(--text-dim)" fontSize={10} />
                <Tooltip contentStyle={{ background: "var(--bg-surface)", border: "1px solid var(--border-strong)", fontFamily: "'DM Mono', monospace", fontSize: 11 }} />
                <Bar dataKey="count" fill="#185FA5" />
                <Bar dataKey="won_count" fill="#22a35a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Top 10 customers">
          <div className="space-y-1" data-testid="stats-table-customers">
            {(d.by_customer || []).map((c, i) => (
              <div key={c.customer_name} className="flex items-center justify-between px-2 py-1.5 rounded" style={{ background: "var(--bg-elevated)", border: "1px solid var(--border-strong)" }}>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dim)" }}>{i + 1}. {c.customer_name}</span>
                <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, color: "var(--text-primary)" }}>{c.count} · {Number(c.amount).toLocaleString()}</span>
              </div>
            ))}
            {(d.by_customer || []).length === 0 && <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text-dimmer)", textAlign: "center", padding: 16 }}>No data</div>}
          </div>
        </Card>
      </div>
    </div>
  );
};

const KpiTile = ({ label, value, accent, testid }) => (
  <div className="rounded-md p-3" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-strong)", borderLeft: `3px solid ${accent}` }} data-testid={testid}>
    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "var(--text-dimmer)", letterSpacing: "1.5px", textTransform: "uppercase" }}>{label}</div>
    <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 26, color: accent, marginTop: 4 }}>{value}</div>
  </div>
);

// ───────────────────────────── Key Account dashboard (iter86b C9) ─────────────────────────────

const KeyAccountTab = () => {
  const cats = useLogisticsCategories();
  const keyCats = (cats.data?.items || []).filter((c) => c.is_key_account && !c.archived);
  if (cats.isLoading) {
    return <Empty>Loading…</Empty>;
  }
  if (keyCats.length === 0) {
    return (
      <div className="rounded-md p-6 text-center" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-strong)" }} data-testid="logistics-key-account-empty">
        <Crown size={32} style={{ color: "#b7770d", margin: "0 auto 8px" }} />
        <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text-primary)", marginBottom: 4 }}>No Key Account categories yet</div>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text-dimmer)" }}>Mark a customer category as Key Account in /admin/logistics-categories.</div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="logistics-key-account">
      {keyCats.map((c) => <KeyAccountCategoryBlock key={c.id} cat={c} />)}
    </div>
  );
};

const KeyAccountCategoryBlock = ({ cat }) => {
  const q = useLogisticsStats({ customer_category_id: cat.id });
  const d = q.data || { totals: {}, by_customer: [] };
  return (
    <div className="rounded-md p-3" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-strong)" }} data-testid={`logistics-key-account-cat-${cat.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <Crown size={14} style={{ color: "#b7770d" }} />
        <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>{cat.name}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
        <KpiTile label="Total" value={d.totals.total_count ?? 0} accent="#185FA5" testid={`ka-kpi-total-${cat.id}`} />
        <KpiTile label="Won" value={d.totals.won_count ?? 0} accent="#22a35a" testid={`ka-kpi-won-${cat.id}`} />
        <KpiTile label="Win rate" value={`${d.totals.win_rate_pct ?? 0}%`} accent="#b7770d" testid={`ka-kpi-winrate-${cat.id}`} />
        <KpiTile label="Total amount" value={Number(d.totals.total_amount || 0).toLocaleString()} accent="#7e3aa8" testid={`ka-kpi-amount-${cat.id}`} />
      </div>
      <div className="space-y-1">
        {(d.by_customer || []).slice(0, 5).map((c) => (
          <div key={c.customer_name} className="flex items-center justify-between px-2 py-1 rounded" style={{ background: "var(--bg-elevated)" }}>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dim)" }}>{c.customer_name}</span>
            <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 11, color: "var(--text-primary)" }}>{c.count} · {Number(c.amount).toLocaleString()}</span>
          </div>
        ))}
        {(d.by_customer || []).length === 0 && <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)", textAlign: "center", padding: 8 }}>No deals yet for this category</div>}
      </div>
    </div>
  );
};
